import { FunctionComponent, useContext, useEffect, useState } from 'react';

import AppContext from 'App/AppContext';
import { IPCheckbox, IPLabel, IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import HrefLink from 'components/Links/HrefLink';
import EntityPicker from 'components/Pickers/EntityPicker';
import SingleTask from 'components/Tasks/SingleTask';
import { TaskDateLinkWithHoverCard } from 'components/Tasks/View/TaskDateLinkWithHoverCard';
import { TaskViewScheduleLevel } from 'components/Tasks/View/TasksView';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalStackTokensGapSmall } from 'globalStyles';
import Entity, { EntityTypes } from 'models/entity';
import Task from 'models/tasks/task';
import { useTranslation } from 'react-i18next';
import { apiGetSingleTask, apiGetTaskInstancesByDate, apiGetTasks } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { addDateYears, toLocaleDateTimeMedium } from 'utils/datetime';
import { htmlToText } from 'utils/html';
import { overflow } from 'utils/string';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetSingleTaskConfig {
  taskId: number;

  showBasics: boolean;

  showDescription: boolean;

  showSchedule: boolean;

  constructor() {
    this.taskId = 0;
    this.showBasics = true;
    this.showDescription = true;
    this.showSchedule = true;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.taskId = newRawConfig.taskId ?? 0;
        this.showBasics = newRawConfig.showBasics ?? false;
        this.showDescription = newRawConfig.showDescription ?? false;
        this.showSchedule = newRawConfig.showSchedule ?? false;

        //enable showBasics if all are false
        if (!this.showBasics && !this.showDescription && !this.showSchedule) {
          this.showBasics = true;
        }
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetSingleTaskConfig {
    const newConfig = new WidgetSingleTaskConfig();
    newConfig.taskId = this.taskId;
    newConfig.showBasics = this.showBasics;
    newConfig.showDescription = this.showDescription;
    newConfig.showSchedule = this.showSchedule;

    return newConfig;
  }
}

interface IWidgetSingleTaskProps extends IWidgetRendererProps {}

const WidgetSingleTask: FunctionComponent<IWidgetSingleTaskProps> = (props: IWidgetSingleTaskProps) => {
  const { t } = useTranslation(['task', 'translation', 'control']);
  const appContext = useContext(AppContext);
  const [task, setTask] = useState<Task | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [taskInstances, setTaskInstances] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);
  const [config, setConfig] = useState<WidgetSingleTaskConfig | undefined>(undefined);

  useEffect(() => {
    const loadConfig = (): WidgetSingleTaskConfig => {
      const newConfig = new WidgetSingleTaskConfig();
      newConfig.load(props.widget.widgetConfig);

      return newConfig;
    };

    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);
        setTask(undefined);
        setConfig(undefined);

        const config = loadConfig();
        setConfig(config);

        if (config && config.taskId) {
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          const taskCol = await apiGetSingleTask(config.taskId, true, accessToken, appContext.globalDataCache);
          if (taskCol && taskCol.tasks.length > 0) {
            const task = taskCol.tasks[0];
            setTask(task);
            if (task.isSeries()) {
              //get all instances in the current year
              const start = new Date();
              start.setMonth(0);
              start.setDate(0);
              const end = addDateYears(start, 1);
              const tasks = await apiGetTaskInstancesByDate(
                task.taskId,
                start,
                end,
                12,
                accessToken,
                appContext.globalDataCache,
              );
              setTaskInstances(tasks);
            }
          }
        }
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const saveTask = (updatedTask: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled && !isNew) {
      if (task && task.taskId === updatedTask.taskId) {
        setTask(updatedTask);
      }
      const newInstances = taskInstances.map((i) => (i.taskId === updatedTask.taskId ? updatedTask : i));
      setTaskInstances(newInstances);
    }
  };

  const removeTask = (task: Task) => {
    setTaskInstances(taskInstances.filter((t) => t.taskId !== task.taskId));
    setSelectedTask(undefined);
  };

  const updateTask = async (task: Task) => {
    setSelectedTask(undefined);
  };

  const openTask = (task: Task) => {
    setSelectedTask(task);
  };

  const onRenderPrimaryText = (task: Task) => {
    return (
      <IPText>
        <HrefLink href={`/tasks/${task.taskId}`} onClick={() => openTask(task)}>
          {overflow(task.name, 80)}
        </HrefLink>
      </IPText>
    );
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!task) {
    //config is invalid
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:SingleTask.Config.Invalid')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      {config && config.showBasics && (
        <IPStack.Item>
          <IPTooltipHost
            directionalHint={IPDirectionalHint.bottomLeftEdge}
            content={task.isAssigned() ? appContext.globalDataCache.users.get(task.userId).name : undefined}
          >
            <IPPersona
              size={IPPersonaSize.size40}
              text={appContext.globalDataCache.users.get(task.userId).name}
              imageUrl={appContext.globalDataCache.users.getPhotoURLObj(task.userId)}
              onRenderPrimaryText={() => onRenderPrimaryText(task)}
              secondaryText={
                task.isSeries()
                  ? task.recurringPattern.getShortSummary()
                  : appContext.globalDataCache.taskStates.get(task.taskStateId).state
              }
            />
          </IPTooltipHost>
        </IPStack.Item>
      )}
      {config && config.showDescription && (
        <IPStack.Item>
          <IPText>{overflow(htmlToText(task.description), 200)}</IPText>
        </IPStack.Item>
      )}
      {config && config.showSchedule && task.isSeries() && (
        <ScrollableStackItem isOnPanel={false}>
          {taskInstances.map((task: Task) => {
            return (
              <TaskDateLinkWithHoverCard
                key={task.taskId}
                task={task}
                onTaskClick={openTask}
                level={TaskViewScheduleLevel.Month}
              />
            );
          })}
        </ScrollableStackItem>
      )}
      {config && config.showSchedule && !task.isSeries() && (
        <IPStack.Item grow>
          <IPLabel>{t('widgets:SingleTask.Start')}</IPLabel>
          <IPText>{toLocaleDateTimeMedium(task.startDateTime)}</IPText>
          <IPLabel>{t('widgets:SingleTask.Deadline')}</IPLabel>
          <IPText>{toLocaleDateTimeMedium(task.deadline)}</IPText>
        </IPStack.Item>
      )}
      {selectedTask && (
        <SingleTask
          isOpen={true}
          close={() => {
            setSelectedTask(undefined);
          }}
          task={selectedTask}
          onSave={saveTask}
          onRemove={removeTask}
          onUpdate={updateTask}
        />
      )}
    </IPStack>
  );
};

export default WidgetSingleTask;

//
// Config
//

interface IWidgetConfigSingleTaskProps extends IWidgetConfigRendererProps {}

export const WidgetConfigSingleTask: FunctionComponent<IWidgetConfigSingleTaskProps> = (
  props: IWidgetConfigSingleTaskProps,
) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [taskEntities, setTaskEntities] = useState<Entity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<Entity | undefined>(undefined);

  const loadConfig = (): WidgetSingleTaskConfig => {
    const newConfig = new WidgetSingleTaskConfig();
    newConfig.load(props.widget.widgetConfig);

    return newConfig;
  };

  const [config, setConfig] = useState<WidgetSingleTaskConfig>(loadConfig());
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const taskCol = await apiGetTasks(false, undefined, undefined, accessToken, appContext.globalDataCache);

      const entities = getEntitiesFromTasks(taskCol.tasks);
      setTaskEntities(entities);

      if (config.taskId) {
        const entity = entities.find((e) => e.entityId === config.taskId);
        setSelectedEntity(entity);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getEntitiesFromTasks = (tasks: Task[]): Entity[] => {
    const entities: Entity[] = [];

    entities.push(
      ...tasks.map((c) => {
        const newEntity = new Entity();
        newEntity.entityId = c.taskId;
        newEntity.typeOfEntity = EntityTypes.Task;
        newEntity.entityName = c.name;

        return newEntity;
      }),
    );

    return entities;
  };

  const isConfigValid = (config: WidgetSingleTaskConfig): boolean => {
    if (config.taskId <= 0) return false;

    return true;
  };

  const updateConfig = (config: WidgetSingleTaskConfig) => {
    const newConfig = config.clone();
    setConfig(newConfig);
    props.onUpdateConfig(JSON.stringify(newConfig), isConfigValid(newConfig));
  };

  const onAddSelectedEntity = (entity: Entity) => {
    setSelectedEntity(entity);
    config.taskId = entity.entityId;
    updateConfig(config);
  };

  const onRemoveSelectedEntity = () => {
    setSelectedEntity(undefined);
    config.taskId = 0;
    updateConfig(config);
  };

  const setShowBasics = (checked: boolean | undefined) => {
    if (checked === undefined) return;
    config.showBasics = checked;
    updateConfig(config);
  };

  const setShowDescription = (checked: boolean | undefined) => {
    if (checked === undefined) return;
    config.showDescription = checked;
    updateConfig(config);
  };

  const setShowSchedule = (checked: boolean | undefined) => {
    if (checked === undefined) return;
    config.showSchedule = checked;
    updateConfig(config);
  };

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPCheckbox
          disabled={isLoading}
          label={t('widgets:SingleTask.Config.ShowBasics.Label')}
          checked={config.showBasics}
          onChange={(ev, checked) => setShowBasics(checked)}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          disabled={isLoading}
          label={t('widgets:SingleTask.Config.ShowDescription.Label')}
          checked={config.showDescription}
          onChange={(ev, checked) => setShowDescription(checked)}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          disabled={isLoading}
          label={t('widgets:SingleTask.Config.ShowSchedule.Label')}
          checked={config.showSchedule}
          onChange={(ev, checked) => setShowSchedule(checked)}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPLabel>{t('widgets:SingleTask.Config.Task.Label')}</IPLabel>
        <EntityPicker
          entities={taskEntities}
          isLoading={isLoading}
          addSelectedEntity={onAddSelectedEntity}
          clearSearchText={onRemoveSelectedEntity}
          loadData={loadData}
          setSearchTextToSelectedEntity={true}
          selectedEntity={selectedEntity}
          isOnPanel={true}
        ></EntityPicker>
      </IPStack.Item>
    </IPStack>
  );
};
